import { BodyType, Car, CarPhotos } from '../types/car';
import { DEFAULT_INSTALLMENTS, calcInstallment } from './financing';

type Row = Record<string, string>;

type Photo = { key: string; url: string };

type Draft = Omit<Car, 'photos' | 'image'> & { photoRefs: { front?: Photo; side?: Photo; interior?: Photo } };

/** Normaliza encabezados: "Versión" → "version", "Año" → "ano". */
export function normalizeHeader(header: string): string {
  return header
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function pick(row: Row, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v && v.trim()) return v.trim();
  }
  return '';
}

function num(raw: string): number {
  const v = raw.replace(/[^\d,.-]/g, '');
  if (!v) return NaN;
  if (v.includes(',')) return Number(v.replace(/\./g, '').replace(',', '.'));
  if (/^\d{1,3}(\.\d{3})+$/.test(v)) return Number(v.replace(/\./g, ''));
  return Number(v);
}

const isNo = (v: string) => /^no\b/i.test(v.trim());
const isYes = (v: string) => /^s[ií]\b/i.test(v.trim());
const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

/** Convierte un enlace de Google Drive en una imagen directa. Devuelve undefined si no es una foto válida. */
function toPhoto(value: string): Photo | undefined {
  const drive = value.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:export=\w+&)?id=)([\w-]{20,})/);
  if (drive) return { key: drive[1], url: `https://lh3.googleusercontent.com/d/${drive[1]}=w1000` };
  if (/^https:\/\/\S+\.(jpe?g|png|webp)(\?\S*)?$/i.test(value)) return { key: value, url: value };
  return undefined;
}

function toBodyType(v: string): BodyType {
  const s = v.toLowerCase();
  if (/hatch|hash/.test(s)) return 'Hatchback';
  if (/suv/.test(s)) return 'SUV';
  if (/sed[aá]n/.test(s)) return 'Sedán';
  if (/pick|camioneta/.test(s)) return 'Pickup';
  return 'Otro';
}

function toDraft(row: Row, line: number, issues: string[]): Draft | null {
  const id = pick(row, 'id').toUpperCase();
  if (!id) return null;
  if (!isYes(pick(row, 'disponible'))) return null;

  const currency = (pick(row, 'moneda') || 'ARS').toUpperCase();
  if (currency !== 'ARS') {
    issues.push(`${id}: moneda "${currency}" no soportada todavía`);
    return null;
  }

  const brand = pick(row, 'marca').toUpperCase();
  const model = pick(row, 'modelo').toUpperCase();
  const version = pick(row, 'version');
  const year = num(pick(row, 'ano', 'anio'));
  const km = num(pick(row, 'kilometraje', 'km'));
  const cashPrice = num(pick(row, 'precio', 'precio contado'));
  const rawDown = num(pick(row, 'anticipo'));
  const downPayment = Number.isNaN(rawDown) ? 0 : rawDown;

  const missing = [
    !brand && 'marca',
    !model && 'modelo',
    !(year > 1950) && 'año',
    !(cashPrice > 0) && 'precio',
  ].filter(Boolean);
  if (missing.length) {
    issues.push(`Fila ${line} (${id}): datos inválidos o faltantes (${missing.join(', ')})`);
    return null;
  }
  if (downPayment >= cashPrice) {
    issues.push(`Fila ${line} (${id}): el anticipo es igual o mayor al precio`);
    return null;
  }

  const front = toPhoto(pick(row, 'imagen principal', 'imagen 1'));
  if (!front) {
    issues.push(`Fila ${line} (${id}): falta la imagen principal o el enlace no es válido`);
    return null;
  }

  const cuota = num(pick(row, 'cuota', 'valor cuota'));
  const condition = /0\s?km/i.test(pick(row, 'estado')) || km === 0 ? '0 km' : 'Usado';

  return {
    id,
    brand,
    model,
    version,
    year,
    km: Number.isNaN(km) ? 0 : km,
    condition,
    fuel: capitalize(pick(row, 'combustible')) || '—',
    transmission: capitalize(pick(row, 'transmision')) || '—',
    engine: '—',
    bodyType: toBodyType(pick(row, 'tipo', 'carroceria')),
    color: capitalize(pick(row, 'color')) || '—',
    location: capitalize(pick(row, 'ubicacion')) || '—',
    description: pick(row, 'descripcion corta', 'descripcipcion larga', 'descripcion larga', 'descripcion'),
    cashPrice,
    downPayment,
    installments: DEFAULT_INSTALLMENTS,
    installmentAmount: cuota > 0 ? cuota : calcInstallment(cashPrice - downPayment, DEFAULT_INSTALLMENTS),
    financing: !isNo(pick(row, 'financiacion')),
    tradeIn: !isNo(pick(row, 'permuta')),
    features: [],
    photoRefs: {
      front,
      side: toPhoto(pick(row, 'imagen 2')),
      interior: toPhoto(pick(row, 'imagen 3')),
    },
  };
}

/**
 * Convierte las filas de la planilla en vehículos.
 * - IDs repetidos: se descartan TODAS las filas con ese ID.
 * - Una misma foto usada en dos IDs distintos: se quita de ambos para no mezclar unidades.
 */
export function parseInventoryRows(rows: Row[]): { cars: Car[]; issues: string[] } {
  const issues: string[] = [];
  const drafts = rows.map((row, i) => toDraft(row, i + 2, issues)).filter((d): d is Draft => d !== null);

  const idCount = new Map<string, number>();
  drafts.forEach((d) => idCount.set(d.id, (idCount.get(d.id) ?? 0) + 1));
  idCount.forEach((n, id) => n > 1 && issues.push(`${id}: ID repetido ${n} veces, no se publica`));

  const photoOwners = new Map<string, Set<string>>();
  drafts.forEach((d) =>
    Object.values(d.photoRefs).forEach((p) => {
      if (!p) return;
      const owners = photoOwners.get(p.key) ?? new Set<string>();
      owners.add(d.id);
      photoOwners.set(p.key, owners);
    }),
  );
  const exclusive = (p?: Photo) => (p && (photoOwners.get(p.key)?.size ?? 0) === 1 ? p.url : undefined);

  const cars: Car[] = [];
  drafts.forEach(({ photoRefs, ...d }) => {
    if (idCount.get(d.id) !== 1) return;
    const front = exclusive(photoRefs.front);
    if (!front) {
      issues.push(`${d.id}: la imagen principal también está cargada en otro vehículo`);
      return;
    }
    const photos: CarPhotos = { front, side: exclusive(photoRefs.side), interior: exclusive(photoRefs.interior) };
    cars.push({ ...d, image: front, photos });
  });

  return { cars, issues };
}
